import type { ReactElement, ReactNode } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button } from '../../../../ui-components/button/Button';
import { classNames } from '../../../../utils/tailwind/classNames';

const DEFAULT_INITIAL_NUM_TO_RENDER = 4;
const DEFAULT_MAX_NUM_TO_ROWS = 2;
const GAP_BETWEEN_ITEMS = 8;
const MAX_VISIBLE_ITEMS = 9999;
const ESTIMATED_MORE_BUTTON_WIDTH = 70;

export type RevealMode =
    | { mode: 'none' }
    | { mode: 'horizontal'; initialNumToRender?: number }
    | { mode: 'vertical'; maxNumRows?: number };
export type ListRenderItem<ItemT> = (info: {
    item: ItemT;
    index: number;
}) => React.ReactElement | null;

export type AdaptiveRevealListProps<ItemT> = {
    data: ItemT[];
    keyExtractor?: (item: ItemT, index: number) => string;
    renderItem: ListRenderItem<ItemT> | null | undefined;
    id?: string;
    extraItem?: ReactNode;
} & RevealMode;

export function AdaptiveRevealList<ItemT>({
    data,
    keyExtractor,
    renderItem,
    id,
    extraItem,
    ...props
}: AdaptiveRevealListProps<ItemT>): ReactElement {
    let initialNumToRender = DEFAULT_INITIAL_NUM_TO_RENDER;
    let maxNumRows = DEFAULT_MAX_NUM_TO_ROWS;
    if (props.mode === 'vertical') {
        maxNumRows = props.maxNumRows ?? DEFAULT_MAX_NUM_TO_ROWS;
    } else if (props.mode === 'horizontal') {
        initialNumToRender = props.initialNumToRender ?? DEFAULT_INITIAL_NUM_TO_RENDER;
    }

    const [isExpanded, setIsExpanded] = useState(false);
    const [containerFullAt, setContainerFullAt] = useState<number>(MAX_VISIBLE_ITEMS);
    const [showMoreButtonSpan, setShowMoreButtonSpan] = useState<number>(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const tagRefs = useRef<(HTMLDivElement | null)[]>([]);
    const extraItemRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        if (props.mode === 'horizontal') {
            if (!containerRef.current) return;
            const containerWidth = containerRef.current.offsetWidth;
            let total = 0;
            let visible = 0;
            if (extraItem && extraItemRef.current) {
                total += extraItemRef.current.offsetWidth;
            }
            for (let i = 0; i < data.length && visible < initialNumToRender; i++) {
                const tag = tagRefs.current[i];
                if (!tag) break;
                const tagWidth = tag.offsetWidth;
                const remaining = data.length - (visible + 1);
                const needsMoreButton = remaining > 0;
                const nextTotal =
                    total + tagWidth + (needsMoreButton ? ESTIMATED_MORE_BUTTON_WIDTH : 0);
                if (nextTotal > containerWidth) {
                    break;
                }
                total += tagWidth;
                visible += 1;
            }
            setContainerFullAt(visible);
        }
        if (props.mode === 'vertical') {
            // will try to emulate the flex-wrap algorithm to calculate the number of items per line
            if (!containerRef.current) return;

            const containerWidth = containerRef.current.offsetWidth;
            const rowWidths = Array<number>(maxNumRows).fill(0);
            let visible = 0;
            if (extraItem && extraItemRef.current) {
                rowWidths[0] += extraItemRef.current.offsetWidth;
            }
            let rowWithSpace = 0;
            for (let i = 0; i < data.length; i++) {
                const tag = tagRefs.current[i];
                if (!tag) break;
                const tagWidth = tag.offsetWidth;
                const currentElementWidth =
                    rowWidths[rowWithSpace] > 0 ? GAP_BETWEEN_ITEMS + tagWidth : tagWidth;
                const currentElementFits =
                    rowWidths[rowWithSpace] + currentElementWidth <= containerWidth;

                if (currentElementFits) {
                    // If current element fits, add it to the current row
                    rowWidths[rowWithSpace] += currentElementWidth;
                    visible += 1;
                } else if (rowWithSpace < maxNumRows - 1) {
                    // If current element doesn't fit, move to the next row
                    rowWithSpace += 1;
                    rowWidths[rowWithSpace] += tagWidth;
                    visible += 1;
                } else {
                    // If not all tags fit, reserve space for the '+ X More' button in the last row
                    const _containerFullAt = visible;
                    setContainerFullAt(_containerFullAt);
                    const lastRow = maxNumRows - 1;
                    while (
                        rowWidths[lastRow] + ESTIMATED_MORE_BUTTON_WIDTH > containerWidth &&
                        visible > 0
                    ) {
                        // Remove the last tag
                        visible -= 1;
                        const lastTag = tagRefs.current[visible];
                        if (!lastTag) break;
                        rowWidths[lastRow] -= lastTag.offsetWidth;
                    }

                    setShowMoreButtonSpan(_containerFullAt - visible);
                }
            }
        }
    }, [data.length, props.mode, initialNumToRender, extraItem, maxNumRows]);

    const showMore = !isExpanded && data.length > containerFullAt;
    const showLess = isExpanded && data.length > containerFullAt;
    return (
        <>
            <div
                ref={containerRef}
                data-testid={`${id ? `${id}-` : ''}list-reveal-items`}
                role="list"
                className={classNames('flex gap-2', {
                    'scrollbar-hide overflow-x-scroll': props.mode === 'none',
                    'flex-wrap': props.mode !== 'none',
                })}
            >
                {extraItem ? <div ref={extraItemRef}>{extraItem}</div> : null}
                {data.map((item, index) => {
                    const key = keyExtractor ? keyExtractor(item, index) : index.toString();
                    return (
                        <div
                            key={key}
                            role="listitem"
                            className={classNames({
                                hidden:
                                    showMore && index > containerFullAt - showMoreButtonSpan - 1,
                            })}
                        >
                            {renderItem ? renderItem({ item, index }) : null}
                        </div>
                    );
                })}
                {showMore ? (
                    <Button
                        variant="text-button-no-underline"
                        onClick={() => setIsExpanded(true)}
                        id={`${id ? `${id}-` : ''}list-reveal-show-more-button`}
                        trim
                    >
                        {`+ ${data.length - containerFullAt + showMoreButtonSpan} More`}
                    </Button>
                ) : null}
                {showLess ? (
                    <Button
                        variant="text-button-no-hover"
                        onClick={() => setIsExpanded(false)}
                        id={`${id ? `${id}-` : ''}list-reveal-show-less-button`}
                        trim
                    >
                        <FormattedMessage id="list-reveal.show-less" defaultMessage="Show Less" />
                    </Button>
                ) : null}
            </div>
            {/* This is a hidden div that is used to calculate the width of the children of the container, since until it is rendered is when we know widths, which is the basis of determining this interaction */}
            <div
                id="vertical-list-reveal-hidden-for-measurements"
                className={classNames(
                    'invisible absolute left-[-9999px] top-[-9999px] flex gap-2',
                    {
                        'scrollbar-hide overflow-x-scroll': props.mode === 'none',
                        'flex-wrap': props.mode !== 'none',
                    },
                )}
                style={{ width: containerRef.current?.offsetWidth }}
            >
                {data.map((item, index) => {
                    const key = keyExtractor ? keyExtractor(item, index) : index.toString();
                    return (
                        <div
                            key={key}
                            role="listitem"
                            ref={(el) => {
                                tagRefs.current[index] = el;
                            }}
                        >
                            {renderItem ? renderItem({ item, index }) : null}
                        </div>
                    );
                })}
            </div>
        </>
    );
}

AdaptiveRevealList.defaultProps = {
    id: undefined,
    keyExtractor: undefined,
    extraItem: null,
};
